import { memo, useCallback } from "react";

import type { DiscountTargetType, LongTermStayDiscountObjectInput } from "@/lib/gql/__generated__/graphql";
import { DiscountType } from "@/lib/gql/__generated__/graphql";

import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";

import { Button } from "@/components/ui/button/button";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";
import { SelectField } from "@/components/ui/form/fields/select-field";

import * as Constants from "./constants";
import { SettingHelpText } from "./setting-help-text";
import { FormFieldLayout } from "../../layouts";

type Props = {
	//
	longTermStayDiscountObject: ForArrayType<Array<LongTermStayDiscountObjectInput>>;
	// 連泊ディスカウントの追加
	onAddLongTermStayDiscountObject: () => void;
	// 連泊ディスカウントの割引方法の変更
	onChangeDiscountType: (targetIndex: number, value: DiscountType) => void;
	// 連泊ディスカウントの泊数の変更
	onChangeIncludeSmallestNights: (targetIndex: number, value: number) => void;
	// 連泊ディスカウントのの固定割引の対象の変更
	onChangeStaticTargetType: (targetIndex: number, value: DiscountTargetType) => void;
	// 連泊ディスカウントの割引の割合額の変更
	onChangeRatio: (targetIndex: number, value: number) => void;
	// 連泊ディスカウントの割引の固定額の変更
	onChangeStatic: (targetIndex: number, value: number) => void;
	// 連泊ディスカウントの削除
	onDeleteLongTermStayDiscountObject: (targetIndex: number) => void;
};

export const LongTermStayFormFieldComposition = memo(
	({
		longTermStayDiscountObject,
		onAddLongTermStayDiscountObject,
		onChangeIncludeSmallestNights: _onChangeIncludeSmallestNights,
		onChangeDiscountType: _onChangeDiscountType,
		onChangeStaticTargetType: _onChangeStaticTargetType,
		onChangeRatio: _onChangeRatio,
		onChangeStatic,
		onDeleteLongTermStayDiscountObject,
	}: Props) => {
		/**
		 * 連泊ディスカウントの追加
		 */
		const handleClickAddLongTermStay = useCallback(() => {
			onAddLongTermStayDiscountObject();
		}, [onAddLongTermStayDiscountObject]);
		/**
		 * 連泊ディスカウントの泊数の変更
		 */
		const onChangeIncludeSmallestNights = useCallback(
			(targetIndex: number) => (value: number | undefined) => {
				_onChangeIncludeSmallestNights(targetIndex, value!);
			},
			[_onChangeIncludeSmallestNights],
		);
		/**
		 * 連泊ディスカウントの割引方法の変更
		 */
		const onChangeDiscountType = useCallback(
			(targetIndex: number) => (value: string) => {
				_onChangeDiscountType(targetIndex, value as DiscountType);
			},
			[_onChangeDiscountType],
		);
		/**
		 * 連泊ディスカウントの割引の割合額の変更
		 */
		const onChangeRatio = useCallback(
			(targetIndex: number) => (value: number | undefined) => {
				_onChangeRatio(targetIndex, value!);
			},
			[_onChangeRatio],
		);
		/**
		 * 連泊ディスカウントの固定割引の対象の変更
		 */
		const onChangeStaticTargetType = useCallback(
			(targetIndex: number) => (value: string) => {
				_onChangeStaticTargetType(targetIndex, value as DiscountTargetType);
			},
			[_onChangeStaticTargetType],
		);
		/**
		 * 連泊ディスカウントの割引の固定額の変更
		 */
		const onChangeStaticAmount = useCallback(
			(targetIndex: number) => (value: number | undefined) => {
				onChangeStatic(targetIndex, value!); // Zodのバリデーションエラーを出すため未入力（undefined）を許容している
			},
			[onChangeStatic],
		);
		/**
		 * 連泊ディスカウントの削除
		 */
		const onClickDeleteLongTermStay = useCallback(
			(targetIndex: number) => () => {
				onDeleteLongTermStayDiscountObject(targetIndex);
			},
			[onDeleteLongTermStayDiscountObject],
		);

		return (
			<FormFieldLayout title="連泊ディスカウント">
				<div className="space-y-3">
					{longTermStayDiscountObject.value.map((value, index) => {
						const key = `${value.discountType.value}-${index}`;
						const handleClickDelete = onClickDeleteLongTermStay(index);
						const handleChangeIncludeSmallestNights = onChangeIncludeSmallestNights(index);
						const handleChangeDiscountType = onChangeDiscountType(index);
						const handleChangeRatio = onChangeRatio(index);
						const handleChangeStaticTargetType = onChangeStaticTargetType(index);
						const handleChangeStaticAmount = onChangeStaticAmount(index);

						return (
							<div key={key} className="w-full rounded border border-zinc-200 p-4">
								{/* 最低一つは設定を明示的に促す */}
								{index !== 0 && (
									<div className="text-right">
										<Button type="button" onClick={handleClickDelete}>
											削除
										</Button>
									</div>
								)}

								<div className="space-y-4">
									<div>
										<p className="text-gray-900 mb-1 block text-sm font-medium">連泊設定</p>
										<div className="flex flex-wrap items-center gap-3">
											<InputNumberField
												key={index}
												value={value.includeSmallestNights.value}
												errorMessage={value.includeSmallestNights.error}
												handleInput={handleChangeIncludeSmallestNights}
												hiddenSpinButton={false}
											/>

											<p>泊目以降</p>

											<div className="flex items-center gap-x-3">
												<SelectField
													value={value.discountType.value}
													options={Constants.DiscountTypeOptions}
													handleValueChange={handleChangeDiscountType}
												/>
												<p>の割引に設定する</p>
											</div>
										</div>
									</div>

									<div>
										<p className="text-gray-900 mb-1 block text-sm font-medium">料金</p>
										{value.discountType.value === DiscountType.Ratio ? (
											<div className="flex items-center gap-x-3">
												<p>通常料金から</p>

												<InputNumberField
													value={value.ratio?.value ?? undefined}
													errorMessage={value.ratio?.error}
													handleInput={handleChangeRatio}
													hiddenSpinButton={false}
												/>

												<p>%割引</p>
											</div>
										) : (
											<div className="flex flex-wrap items-center gap-3">
												<div className="flex items-center gap-x-3">
													<SelectField
														value={value.staticTargetType?.value}
														options={Constants.StaticTargetTypeOptions}
														handleValueChange={handleChangeStaticTargetType}
													/>

													<p>ごとに</p>
												</div>

												<div className="flex items-center gap-x-3">
													<InputNumberField
														handleInput={handleChangeStaticAmount}
														value={value.static?.amount?.value}
														errorMessage={value.static?.amount?.error}
													/>

													<p>円（税込）引き</p>
												</div>
											</div>
										)}
									</div>
								</div>
							</div>
						);
					})}

					<div className="mt-4">
						<p className="whitespace-pre-wrap text-sm text-caption-50">
							設定した日数以降の宿泊日に対してディスカウントが適用されます。
							<SettingHelpText />
						</p>
					</div>

					<Button variant="outline" type="button" onClick={handleClickAddLongTermStay}>
						追加
					</Button>
				</div>
			</FormFieldLayout>
		);
	},
);
